import React, { useEffect, useLayoutEffect, useState } from 'react';
import { matchPath, Routes, useLocation, useNavigate } from 'react-router-dom';
import Header from './header/Header';
import { useModuleSheet } from './content/useModuleSheet';
import Menu, { MENU_WIDTHS } from './menu/Menu';
import { userRoutes, filterMenusWithPlan } from '../routes';
import { useAppContext } from '../contexts/AppContext';
import { useAuthContext } from '../contexts/AuthContext';
import { usePlanAccess } from '../plans/usePlanAccess';
import MenuDrawer from './menu/MenuDrawer';
import { Modal } from '../components/Modal';
import { PromptSheet } from '../components/PromptSheet';
import ConfiguracoesContaPhoneNumber from '../modules/configuracoes/usuario/conta/PhoneNumber';
import { useMediaQuery } from 'react-responsive';
import { useEstabsContext } from '../contexts/EstabsContext';
import { EstabelecimentosService } from '../modules/estabelecimentos/system-admin/Service';
import TrialBanner from '../plans/TrialBanner';
import { Check, ChevronUp, Circle, MessageCircle, X } from 'lucide-react';

type OnboardingStepKey = 'services' | 'agenda' | 'planos' | 'firstSchedule';

const estabelecimentosService = new EstabelecimentosService();

export default function DashboardPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuthContext();
    const { estabSession, reloadEstab } = useEstabsContext();
    const [routes, setRoutes] = useState<any>([]);
    const [openFormPhoneNumber, setOpenFormPhoneNumer] = useState(false);
    const [openOnboardingPrompt, setOpenOnboardingPrompt] = useState(false);
    const [onboardingSaving, setOnboardingSaving] = useState(false);
    const [onboardingDockMinimized, setOnboardingDockMinimized] = useState(false);
    const [onboardingSteps, setOnboardingSteps] = useState<Record<OnboardingStepKey, boolean>>({
        services: false,
        agenda: false,
        planos: false,
        firstSchedule: false,
    });
    const { showDrawerMenu, toggleShowDrawerMenu, shortMenu, subMenu, successSnackbar, errorSnackbar, isScrolled } =
        useAppContext();
    const isCompactMenu = useMediaQuery({ maxWidth: 1024 });
    const { menuShowEntry, resolvedPlanKey, billingStatus, billingExempt, trialRemaining } = usePlanAccess();
    const showMenu = user ? filterMenusWithPlan(user, menuShowEntry).length > 1 : false;
    const menuWidth = shortMenu ? MENU_WIDTHS.collapsed : MENU_WIDTHS.expanded;
    const subMenuWidth = subMenu ? MENU_WIDTHS.subMenu + MENU_WIDTHS.gap : 0;
    const contentOffset = menuWidth + subMenuWidth + MENU_WIDTHS.offset;
    const isWhatsappRoute = location.pathname === '/whatsapp';
    // Rota inexistente nao tem cabecalho proprio nem como voltar: mantem o header global.
    const isKnownRoute = routes.some(
        (route: any) => route.props.path !== '*' && !!matchPath(route.props.path, location.pathname),
    );
    const moduleSheet = useModuleSheet() && isKnownRoute;

    // A pagina rola na janela: sem isso, o modulo abriria na altura em que a Agenda estava.
    useLayoutEffect(() => {
        if (moduleSheet) {
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [location.pathname, moduleSheet]);

    useEffect(() => {
        if (user) {
            setRoutes(userRoutes(user));
        }
    }, [user]);

    useEffect(() => {
        const modalShownTime = localStorage.getItem('modalShownTime');

        if (!openFormPhoneNumber && !user?.phoneNumber && !!user?.client) {
            if (modalShownTime) {
                const timeElapsed = Date.now() - parseInt(modalShownTime, 10);
                if (timeElapsed > 5 * 60 * 1000) {
                    setOpenFormPhoneNumer(true);
                    localStorage.setItem('modalShownTime', Date.now().toString());
                }
            } else {
                setOpenFormPhoneNumer(true);
                localStorage.setItem('modalShownTime', Date.now().toString());
            }
        }
    }, [openFormPhoneNumber, user?.phoneNumber]);

    useEffect(() => {
        const steps = (estabSession as any)?.onboarding?.steps || {};
        setOnboardingSteps({
            services: !!steps.services,
            agenda: !!steps.agenda,
            planos: !!steps.planos,
            firstSchedule: !!steps.firstSchedule,
        });
    }, [estabSession]);

    useEffect(() => {
        const estabId = (estabSession as any)?._id;
        const shouldPrompt = !!(
            !!estabId &&
            !!(estabSession as any)?.onboardingInitialPending &&
            (user?.admin || user?.root)
        );

        setOpenOnboardingPrompt(shouldPrompt);
    }, [estabSession, user?.admin, user?.root]);

    useEffect(() => {
        setOnboardingDockMinimized(false);
    }, []);

    const dismissOnboardingPrompt = async () => {
        const estabId = (estabSession as any)?._id;
        if (!estabId || !user?.uid) return;

        setOnboardingSaving(true);
        try {
            await estabelecimentosService.update(estabId, {
                onboardingInitialPending: false,
                onboarding: {
                    ...(estabSession as any)?.onboarding,
                    completedAt: new Date().toISOString(),
                    completedBy: user.uid,
                    steps: onboardingSteps,
                },
            });
            await reloadEstab();
            setOpenOnboardingPrompt(false);
            successSnackbar('Onboarding inicial concluido com sucesso.');
        } catch (error: any) {
            console.error(error);
            errorSnackbar('Não foi possível salvar o onboarding. Tente novamente.');
        } finally {
            setOnboardingSaving(false);
        }
    };

    const saveOnboardingProgress = async (nextSteps: Record<OnboardingStepKey, boolean>) => {
        const estabId = (estabSession as any)?._id;
        if (!estabId) return;

        setOnboardingSaving(true);
        try {
            await estabelecimentosService.update(estabId, {
                onboarding: {
                    startedAt: (estabSession as any)?.onboarding?.startedAt || new Date().toISOString(),
                    completedAt: (estabSession as any)?.onboarding?.completedAt,
                    completedBy: (estabSession as any)?.onboarding?.completedBy,
                    steps: nextSteps,
                },
            });
            await reloadEstab();
            successSnackbar('Progresso do onboarding salvo.');
        } catch (error: any) {
            console.error(error);
            errorSnackbar('Não foi possível salvar o progresso do onboarding.');
        } finally {
            setOnboardingSaving(false);
        }
    };

    const toggleStep = async (step: OnboardingStepKey) => {
        if (onboardingSteps[step]) {
            return;
        }

        const nextSteps = {
            ...onboardingSteps,
            [step]: true,
        };
        setOnboardingSteps(nextSteps);
        await saveOnboardingProgress(nextSteps);
    };

    const stepItems: Array<{
        key: OnboardingStepKey;
        title: string;
        description: string;
        route: string;
        cta: string;
        canSkip?: boolean;
    }> = [
        {
            key: 'services',
            title: 'Cadastre serviços principais',
            description: 'Adicione ou ajuste os serviços para começar a receber agendamentos.',
            route: '/servicos?action=quick-add',
            cta: 'Criar serviço',
        },
        {
            key: 'agenda',
            title: 'Configure agenda e horários',
            description: 'Defina horários de funcionamento, regras e intervalos da agenda.',
            route: '/agenda?action=quick-hours',
            cta: 'Configurar agenda',
        },
        {
            key: 'planos',
            title: 'Organize planos e ofertas',
            description: 'Estruture planos para assinatura e recorrência dos clientes.',
            route: '/planos?action=add',
            cta: 'Criar plano',
            canSkip: true,
        },
        {
            key: 'firstSchedule',
            title: 'Crie o primeiro agendamento',
            description: 'Valide o fluxo completo da agenda com um agendamento de teste.',
            route: '/agenda?action=add',
            cta: 'Novo agendamento',
            canSkip: true,
        },
    ];

    const completedCount = Object.values(onboardingSteps).filter(Boolean).length;
    const progressPercent = Math.round((completedCount / stepItems.length) * 100);
    const shouldShowOnboardingDock = !!(
        (estabSession as any)?._id &&
        (user?.admin || user?.root) &&
        (estabSession as any)?.onboardingInitialPending &&
        progressPercent < 100
    );
    const shouldShowTrialBanner = !!(
        (user?.admin || user?.root) &&
        !billingExempt &&
        billingStatus === 'trial' &&
        resolvedPlanKey === 'trial' &&
        trialRemaining &&
        !trialRemaining.ended
    );
    const shouldReserveOnboardingDockSpace = shouldShowOnboardingDock && !openOnboardingPrompt && !onboardingDockMinimized;
    const contentBottomPadding = shouldReserveOnboardingDockSpace ? (isCompactMenu ? 150 : 130) : 24;
    const onboardingDockOffset = shouldReserveOnboardingDockSpace ? (isCompactMenu ? 150 : 130) : 0;

    return (
        <div
            className={`sf-app-shell ${
                isWhatsappRoute
                    ? 'h-screen overflow-hidden pb-2 pt-2'
                    : `min-h-screen ${!isCompactMenu ? 'pt-4' : ''}`
            }`}
            style={{
                ['--onboarding-dock-offset' as any]: `${onboardingDockOffset}px`,
                // Altura do header movel fixo (pt-3 + h-14); o cabecalho de conteudo gruda logo abaixo.
                ['--sf-content-header-top' as any]: isCompactMenu && !moduleSheet ? '4.25rem' : '0px',
            }}
        >
            <div className="sf-app-shell-aura pointer-events-none" />

            {openOnboardingPrompt && (
                <Modal
                    onClose={() => setOpenOnboardingPrompt(false)}
                    title="Onboarding inicial do estabelecimento"
                    subTitle="Configure rapidamente os módulos essenciais. O progresso fica salvo no sistema e pode ser retomado a qualquer momento."
                >
                    <div className="h-[72vh] max-h-[760px] flex flex-col">
                        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
                            <div className="space-y-3">
                                {stepItems.map((step) => (
                                    <div key={step.key} className="rounded-[1.2rem] border border-white/5 bg-base-100/40 p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="pt-1">
                                                {onboardingSteps[step.key] ? (
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-success-content">
                                                        <Check className="text-xs" aria-hidden="true" />
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-base-300 text-base-content/50">
                                                        <Circle className="text-[8px]" aria-hidden="true" />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="font-semibold text-base-content">{step.title}</div>
                                                    <span
                                                        className={`badge badge-sm ${
                                                            onboardingSteps[step.key]
                                                                ? 'badge-success text-success-content'
                                                                : 'badge-ghost'
                                                        }`}
                                                    >
                                                        {onboardingSteps[step.key] ? 'Concluído' : 'Pendente'}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-base-content/65 mt-1">{step.description}</div>
                                                {!onboardingSteps[step.key] && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-outline mt-3"
                                                            onClick={() => {
                                                                navigate(step.route);
                                                                setOpenOnboardingPrompt(false);
                                                            }}
                                                        >
                                                            {step.cta}
                                                        </button>
                                                        {!!step.canSkip && (
                                                            <button
                                                                className="btn btn-sm btn-ghost mt-3 ml-2"
                                                                onClick={() => toggleStep(step.key)}
                                                                disabled={onboardingSaving}
                                                            >
                                                                Pular
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="rounded-[1.2rem] border border-white/5 bg-base-100/40 p-4">
                                <div className="text-sm font-semibold text-base-content mb-2">Atalhos rápidos</div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <button
                                        className="btn btn-sm btn-ghost"
                                        onClick={() => {
                                            navigate('/assinaturas');
                                            setOpenOnboardingPrompt(false);
                                        }}
                                    >
                                        Assinaturas
                                    </button>
                                    <button
                                        className="btn btn-sm btn-ghost"
                                        onClick={() => {
                                            navigate('/clientes');
                                            setOpenOnboardingPrompt(false);
                                        }}
                                    >
                                        Clientes
                                    </button>
                                    <button
                                        className="btn btn-sm btn-ghost"
                                        onClick={() => {
                                            navigate('/estoque');
                                            setOpenOnboardingPrompt(false);
                                        }}
                                    >
                                        Estoque
                                    </button>
                                    <button
                                        className="btn btn-sm btn-ghost"
                                        onClick={() => {
                                            navigate('/configuracoes/estabelecimento');
                                            setOpenOnboardingPrompt(false);
                                        }}
                                    >
                                        Estabelecimento
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 border-t border-white/5 bg-base-100/30 pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button
                                    className="btn btn-primary w-full"
                                    disabled={onboardingSaving || completedCount < stepItems.length}
                                    onClick={dismissOnboardingPrompt}
                                >
                                    {onboardingSaving ? 'Salvando...' : 'Concluir onboarding'}
                                </button>
                                <button className="btn btn-ghost w-full" onClick={() => setOpenOnboardingPrompt(false)}>
                                    Continuar depois
                                </button>
                            </div>

                        </div>
                    </div>
                </Modal>
            )}

            {shouldShowOnboardingDock && !openOnboardingPrompt && !onboardingDockMinimized && (
                <div
                    className={`fixed bottom-0 z-40 pb-[calc(0.75rem+env(safe-area-inset-bottom))] ${
                        !isCompactMenu && showMenu ? '' : 'left-0 right-0 px-3'
                    }`}
                    style={!isCompactMenu && showMenu ? { left: `${contentOffset}px`, right: '1rem' } : undefined}
                >
                    <div className="sf-overlay-panel sf-onboarding-dock mx-auto max-w-5xl p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-base font-semibold text-base-content">Progresso inicial</div>
                            <div className="flex items-center gap-2">
                                <div className="text-xs text-base-content/70">
                                    {completedCount}/{stepItems.length} etapas
                                </div>
                                <button
                                    type="button"
                                    className="sf-icon-button sf-close-button btn btn-circle btn-xs"
                                    aria-label="Minimizar progresso"
                                    onClick={() => {
                                        setOnboardingDockMinimized(true);
                                    }}
                                >
                                    <X aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                        <div className="w-full h-2 rounded-full bg-base-300 overflow-hidden">
                            <div
                                className="h-2 sf-progress-accent transition-all duration-300"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <div className="flex items-center justify-between mt-3 gap-2">
                            <div className="text-xs text-base-content/70">
                                Continue a configuração para finalizar seu onboarding.
                            </div>
                            <button className="btn btn-sm btn-primary" onClick={() => setOpenOnboardingPrompt(true)}>
                                Retomar onboarding
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {shouldShowOnboardingDock && !openOnboardingPrompt && onboardingDockMinimized && (
                <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-40">
                    <button
                        type="button"
                        className="btn btn-primary btn-sm sm:btn-md rounded-full px-3 sm:px-5 text-sm"
                        onClick={() => {
                            setOnboardingDockMinimized(false);
                        }}
                    >
                        <ChevronUp className="mr-1" aria-hidden="true" />
                        Expandir progresso
                    </button>
                </div>
            )}

            {openFormPhoneNumber && (
                <PromptSheet
                    icon={MessageCircle}
                    tone="whatsapp"
                    title="Ative seu WhatsApp"
                    description="Acompanhe seus horários por onde você já conversa."
                    onClose={() => setOpenFormPhoneNumer(false)}
                >
                    <ConfiguracoesContaPhoneNumber variant="prompt" onDismiss={() => setOpenFormPhoneNumer(false)} />
                </PromptSheet>
            )}

            {isCompactMenu && (
                // Fica montado mesmo oculto para nao perder o estado das notificacoes ao trocar de modulo.
                <div
                    className={`sf-mobile-app-header fixed left-0 right-0 z-50 w-full px-3 pt-3 ${
                        isScrolled ? 'is-scrolled' : ''
                    } ${moduleSheet ? 'hidden' : ''}`}
                >
                    <Header />
                </div>
            )}

            <div className="relative flex">
                {showMenu && !isCompactMenu && (
                    <div className="fixed left-4 top-4 !z-50">
                        <Menu />
                    </div>
                )}

                {isCompactMenu && (
                    <div className="pointer-events-none fixed inset-0 z-[1000]">
                        <div className="drawer h-full w-full">
                            <input id="my-drawer" type="checkbox" className="drawer-toggle" checked={showDrawerMenu} readOnly />
                            <div className={`drawer-side z-[1001] ${showDrawerMenu ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                                <label
                                    htmlFor="my-drawer"
                                    aria-label="Fechar menu"
                                    className="drawer-overlay"
                                    onClick={toggleShowDrawerMenu}
                                ></label>
                                <MenuDrawer />
                            </div>
                        </div>
                    </div>
                )}

                <div
                    data-page-scroll
                    className={`w-full ${
                        // `overflow-x-clip` nao cria container de rolagem, preservando o sticky do cabecalho.
                        isWhatsappRoute ? 'overflow-hidden' : 'overflow-x-clip'
                    } ${
                        isCompactMenu
                            ? `${isWhatsappRoute ? 'mx-2' : 'mx-3'} ${moduleSheet ? '' : 'mt-20'} ${isWhatsappRoute ? 'pt-0' : 'pt-2'}`
                            : isWhatsappRoute
                                ? 'px-0'
                                : 'px-2 2xl:px-3'
                    }`}
                    style={{
                        ...(!isCompactMenu && showMenu ? { marginLeft: `${contentOffset}px` } : {}),
                        paddingBottom: isWhatsappRoute ? 0 : `calc(${contentBottomPadding}px + env(safe-area-inset-bottom))`,
                    }}
                >
                    {!!routes?.length && (
                        // WhatsApp fica sem a animacao: mede a propria altura pela posicao na tela ao montar.
                        <div
                            className={`w-full max-w-none min-w-0 ${isWhatsappRoute ? 'pb-0' : 'pb-6'} ${
                                moduleSheet && !isWhatsappRoute ? 'sf-module-sheet' : ''
                            }`}
                        >
                            {shouldShowTrialBanner && trialRemaining && (
                                <TrialBanner planKey={resolvedPlanKey} trialRemaining={trialRemaining} />
                            )}
                            <Routes>{routes}</Routes>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
